import { useState } from 'react';
import './DataSelector.css';
import type { AvailableData } from './types';
import { DataSelectorLaneFilter } from './DataSelectorLaneFilter';

const BIGRANK_LABELS: Record<number, string> = {
  5: 'エピック',
  6: 'レジェンド',
  7: 'ミシック',
  8: 'Hミシック',
  9: 'Gミシック以上',
  101: '全て'
};

interface DataSelectorProps {
  bigrank: number;
  beta: number;
  scrapedAt: string | null;
  availableData: AvailableData[];
  onBigrankChange: (bigrank: number) => void;
  onBetaChange: (beta: number) => void;
  selectedLane: string;
  onLaneChange: (lane: string) => void;
  /** セクション内など余白を詰めたいとき */
  variant?: 'default' | 'embedded';
}

export default function DataSelector({
  bigrank,
  beta,
  scrapedAt,
  availableData,
  onBigrankChange,
  onBetaChange,
  selectedLane,
  onLaneChange,
  variant = 'default',
}: DataSelectorProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const showBetaPanel = variant === 'embedded' || showAdvanced;

  // Filter only past_1_days data and get unique bigranks
  const past1DayData = availableData.filter(d => d.period === 'past_1_days');
  const availableBigranks = [...new Set(past1DayData.map(d => d.bigrank))].sort((a, b) => a - b);

  // Format scraped_at date (convert to JST by adding 9 hours, show date only)
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    // Add 9 hours to convert to JST
    date.setHours(date.getHours() + 9);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className={`data-selector${variant === 'embedded' ? ' data-selector--embedded' : ''}`}>
      <div className="data-selector__filters">
        <div className="selector-group">
          <label>ランク帯</label>
          <select
            value={bigrank}
            onChange={(e) => onBigrankChange(Number(e.target.value))}
            className="selector-dropdown"
            disabled={availableBigranks.length === 0}
          >
            {availableBigranks.length === 0 ? (
              <option value="">データがありません</option>
            ) : (
              availableBigranks.map(b => (
                <option key={b} value={b}>
                  {BIGRANK_LABELS[b] || `Rank ${b}`}
                </option>
              ))
            )}
          </select>
        </div>

        <DataSelectorLaneFilter selectedLane={selectedLane} onLaneChange={onLaneChange} />
      </div>

      <p className="data-selector__lane-note" role="note">
        レーンを変えても、勝率・ピック率・BAN率などの数値は変わりません。あくまでヒーローを絞り込む機能を提供しているに過ぎない点に注意してください。
      </p>

      <div className="data-selector__meta-row">
        {variant !== 'embedded' && (
          <div className="selector-group advanced-toggle-group">
            <button
              className="advanced-toggle-button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              type="button"
            >
              {showAdvanced ? '▲ 高度な設定を隠す' : '▼ 高度な設定を表示'}
            </button>
          </div>
        )}
        {scrapedAt && (
          <div className="selector-group date-group">
            <label>いつ調査したデータか</label>
            <span className="scraped-date">{formatDate(scrapedAt)}</span>
          </div>
        )}
      </div>

      {showBetaPanel && (
        <div className="selector-group beta-group">
          <label className="beta-label-with-tooltip">
            β (補正係数): <span className="beta-value">{beta.toFixed(2)}</span>
            {variant !== 'embedded' && (
              <span
                className="beta-info-icon"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onClick={() => setShowTooltip(!showTooltip)}
              >
                ⓘ
                {showTooltip && (
                  <div className="beta-tooltip">
                    <p>
                      β やティア算出の考え方の詳細は、ページ下の
                      <a href="#tier-calculation-detail" className="beta-tooltip-anchor">
                        「ティア算出の詳細が気になる方へ」
                      </a>
                      を参照してください。
                    </p>
                  </div>
                )}
              </span>
            )}
          </label>
          <div className="beta-slider-container">
            <span className="beta-label">0.00</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={beta}
              onChange={(e) => onBetaChange(Number(e.target.value))}
              className="beta-slider"
            />
            <span className="beta-label">1.00</span>
          </div>
          <div className="beta-description">
            <span className="beta-desc-left">← 勝率のみ重視</span>
            <span className="beta-desc-right">バンピック率を強く加味 →</span>
          </div>
        </div>
      )}
    </div>
  );
}
